"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import Switch from "@/components/common/Switch";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/shadcn/components/ui/tabs";
import NotificationItem, {
  type NotificationItemData,
} from "./NotificationItem";
import {
  useNotifications,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useFilters,
} from "common/hooks";
import { Notification } from "common/types/notifications";
import { useCustomToast } from "@/hooks/useCustomToast";
import { bucketUrlPrefix } from "common/config";
import { findImageByName } from "common/utils/general";
import { BusinessTypeFrontend } from "common/hooks/filters/atoms";
import { useI18N } from "common/hooks";
import FocusTrap from "focus-trap-react";
// ============================================================================
// Types
// ============================================================================

export interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onViewAllClick?: () => void;
  onManageClick?: () => void;
  showBackdrop?: boolean;
}

// ============================================================================
// Tab Types
// ============================================================================

type TabValue = "all" | "live_alerts" | "new_information";

// ============================================================================
// Helper Functions
// ============================================================================

// Helper function to format date as relative time (e.g.,"1m","2h","3d")
const getRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMilliseconds = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));

  if (diffInDays > 0) {
    return `${diffInDays}d`;
  } else if (diffInHours > 0) {
    return `${diffInHours}h`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes}m`;
  } else {
    // Note: "Just now" will be handled by i18n in the component
    return "Just now";
  }
};

// Helper function to format date as readable relative time for screen readers
const getReadableRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMilliseconds = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));

  if (diffInDays > 0) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
  } else if (diffInHours > 0) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else {
    return "Just now";
  }
};

// Map API notification type to component type
const mapNotificationType = (
  apiType: "live_alert" | "accessibility_information",
): "live_alert" | "new_info" => {
  return apiType === "accessibility_information" ? "new_info" : apiType;
};

// Map category filter to API notification type
const mapCategoryToApiType = (
  category: TabValue,
): "live_alert" | "accessibility_information" | "all" => {
  if (category === "live_alerts") return "live_alert";
  if (category === "new_information") return "accessibility_information";
  return "all";
};

// Transform API notification to component notification data
const transformNotification = (
  notification: Notification,
  businessTypesFrontend: BusinessTypeFrontend[],
): NotificationItemData => {
  const summary = notification.activity?.summary || "";
  const subject = notification.activity?.subject;
  const venueName = subject?.title || "";

  // Parse summary to extract content parts
  // This is a simple parser - adjust based on actual API response format
  let prefix = "";
  let suffix = "";

  if (notification.notification_type === "live_alert") {
    // You reported Live Alerts"Accesible bathroom out of order" at GAIL's Bakery Cambridge
    // You updated Live Alerts"Accesible bathroom out of order" at GAIL's Bakery Cambridge
    const parts = summary.split(" at");
    if (parts.length > 1) {
      const prefixTextParts = parts[0].split("Live Alerts");
      prefix = (prefixTextParts[1] || "").replace(/"/g, "").trim();
      suffix = parts[1] || venueName;
    } else {
      prefix = "Accessibility alert";
      suffix = venueName || "";
    }
  } else {
    // For accessibility information
    prefix = "New accessibility information";
    const parts = summary.split(" at");
    if (parts.length > 1) {
      suffix = parts[1] || venueName;
    } else {
      suffix = venueName || "";
    }
  }

  // Extract business image and slug from activity
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activityAny = notification.activity as any;
  const business = activityAny?.business;
  const businessPhotoFirst = activityAny?.businessPhotoFirst;

  // Get business slug - try multiple possible field names
  const slug = business?.slug || business?.venue_slug || business?.business_slug || "";

  // Get business photo
  let businessPhoto = "";
  let hasVenuePhoto = false;
  if (businessPhotoFirst?.path) {
    businessPhoto = bucketUrlPrefix + businessPhotoFirst.path;
    hasVenuePhoto = true;
  }

  // Get business type image (fallback)
  const businessTypeImagePath = findImageByName(
    business?.business_type,
    businessTypesFrontend,
  );
  const businessTypeImage = businessTypeImagePath
    ? bucketUrlPrefix + businessTypeImagePath
    : "";

  const images: string[] = [];
  let isPlaceholder = false;

  // Use business photo if available, otherwise fall back to business type image
  if (businessPhoto) {
    images.push(businessPhoto);
  } else if (businessTypeImage) {
    images.push(businessTypeImage);
  } else {
    isPlaceholder = true;
  }

  return {
    id: notification.id.toString(),
    type: mapNotificationType(notification.notification_type),
    isUnread: !notification.is_read,
    isHighlighted: !notification.is_read,
    images,
    content: {
      prefix,
      suffix,
    },
    timestamp: getRelativeTime(notification.created_at),
    createdAt: notification.created_at,
    readableTimestamp: getReadableRelativeTime(notification.created_at),
    slug,
    hasVenuePhoto,
    isPlaceholder,
  };
};

// ============================================================================
// Main Component
// ============================================================================

export function NotificationPanel({
  isOpen,
  onClose,
  onViewAllClick,
  onManageClick,
  showBackdrop = true,
}: NotificationPanelProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const i18n = useI18N();

  // Map category to API notification type
  const apiNotificationType = useMemo(
    () => mapCategoryToApiType(activeTab),
    [activeTab],
  );

  // Use notifications hook with filters - only fetch first page
  const { data, isLoading, error } = useNotifications(
    apiNotificationType,
    unreadOnly,
  );

  // Get business types for business type icon fallback
  const { businessTypesFrontend } = useFilters();

  // Mutation hooks for marking notifications as read
  const { markAllAsRead, isPending: isMarkingAllAsRead } =
    useMarkAllNotificationsAsRead();
  const { markAsRead } = useMarkNotificationAsRead();

  // Toast hook
  const showToast = useCustomToast();

  // Get first page of notifications only (no pagination)
  const notifications = useMemo(() => {
    if (!data?.pages || data.pages.length === 0) return [];
    return data.pages[0]?.notifications || [];
  }, [data]);

  // Transform notifications to component format
  const transformedNotifications = useMemo(() => {
    if (!businessTypesFrontend) return [];
    return notifications.map((notification) =>
      transformNotification(notification, businessTypesFrontend),
    );
  }, [notifications, businessTypesFrontend]);

  // Handle client-side mounting for portal
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Store the trigger element when panel opens
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element (the button that opened the panel)
      triggerElementRef.current = document.activeElement as HTMLElement;
    } else if (triggerElementRef.current) {
      // Return focus to the trigger element when panel closes
      triggerElementRef.current.focus();
      triggerElementRef.current = null;
    }
  }, [isOpen]);

  // Note: No need to manually refetch when tab changes
  // The atom pattern automatically handles query updates when filters change

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll when panel is open (only if backdrop is shown)
      if (showBackdrop) {
        document.body.style.overflow = "hidden";
      }
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, showBackdrop]);

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead(undefined, {
      onSuccess: (data) => {
        showToast(
          data.message || "All notifications marked as read",
          "success",
        );
      },
      onError: (error) => {
        showToast(
          error.message || "Failed to mark all notifications as read",
          "error",
        );
      },
    });
  }, [markAllAsRead, showToast]);

  const handleNotificationClick = useCallback(
    (id: string, slug?: string) => {
      // Navigate to venue details if slug is available
      if (slug) {
        // Close the panel first
        onClose();
        
        // Navigate to venue details
        navigate({
          to: `/venuedetails/${slug}`,
          search: { source: "notifications-page" },
          replace: true,
        } as any);
        
        // Mark notification as read after navigation starts
        markAsRead(id);
      } else {
        // If no slug, just mark as read and close panel
        markAsRead(id);
        onClose();
      }
    },
    [navigate, onClose, markAsRead],
  );

  // Wrapped markAsRead handler for menu actions (includes toast)
  const handleMarkAsReadFromMenu = useCallback(
    (id: string) => {
      markAsRead(id, {
        onSuccess: (data) => {
          showToast(data.message || "Notification marked as read", "success");
        },
        onError: (error) => {
          showToast(
            error.message || "Failed to mark notification as read",
            "error",
          );
        },
      });
    },
    [markAsRead, showToast],
  );

  if (!isOpen || !isMounted) return null;

  // Use portal to render outside parent stacking context
  return createPortal(
    <>
      {/* Backdrop - positioned below navbar, high z-index to appear above other sticky elements */}
      {showBackdrop && (
        <div
          className="fixed inset-0 top-[73px] bg-black/30 z-[1000]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Notification Panel - fixed position with high z-index to appear above other sticky elements */}
      <FocusTrap
        focusTrapOptions={{
          allowOutsideClick: true,
          escapeDeactivates: false,
          initialFocus: false,
          fallbackFocus: () => panelRef.current || document.body,
        }}
      >
        <div
          ref={panelRef}
          className={cn(
            "fixed top-[81px] right-4 z-[1001]",
            "w-[calc(100vw-32px)] md:w-[420px]",
            "bg-bg-brand-secondary-default",
            "shadow-lg rounded-lg overflow-hidden",
            "flex flex-col",
            "max-h-[calc(100vh-100px)]",
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notification-heading"
        >
        {/* Header */}
        <header className="flex items-center min-h-[64px] px-6 py-0 border-b border-border-token-brand-minimal shrink-0">
          <h2 id="notification-heading" className="label-l-bold text-fg-neutral-strong">{i18n.get("notification")}</h2>
        </header>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as TabValue)}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="border-b border-border-token-brand-minimal shrink-0">
            <TabsList className="h-auto bg-transparent p-0 px-3 gap-0">
              <TabsTrigger
                value="all"
                className={cn(
                  "min-h-[44px] min-w-[44px] px-4 py-2 rounded-none border-b-2",
                  "data-[state=active]:border-primary-500 data-[state=active]:bg-transparent",
                  "data-[state=inactive]:border-transparent data-[state=inactive]:bg-transparent",
                  "data-[state=active]:shadow-none",
                  "label-m-semibold data-[state=active]:text-fg-brand-subtle =active]:",
                  "label-m-medium data-[state=inactive]:text-fg-neutral-subtle =inactive]:text-fg-neutral-minimal",
                )}
              >
                {i18n.get("all")}
              </TabsTrigger>
              <TabsTrigger
                value="live_alerts"
                className={cn(
                  "min-h-[44px] min-w-[44px] px-4 py-2 rounded-none border-b-2",
                  "data-[state=active]:border-primary-500 data-[state=active]:bg-transparent",
                  "data-[state=inactive]:border-transparent data-[state=inactive]:bg-transparent",
                  "data-[state=active]:shadow-none",
                  "label-m-semibold data-[state=active]:text-fg-brand-subtle =active]:",
                  "label-m-medium data-[state=inactive]:text-fg-neutral-subtle =inactive]:text-fg-neutral-minimal",
                )}
              >
                {i18n.get("liveAlerts")}
              </TabsTrigger>
              <TabsTrigger
                value="new_information"
                className={cn(
                  "min-h-[44px] min-w-[44px] px-4 py-2 rounded-none border-b-2",
                  "data-[state=active]:border-primary-500 data-[state=active]:bg-transparent",
                  "data-[state=inactive]:border-transparent data-[state=inactive]:bg-transparent",
                  "data-[state=active]:shadow-none",
                  "label-m-semibold data-[state=active]:text-fg-brand-subtle =active]:",
                  "label-m-medium data-[state=inactive]:text-fg-neutral-subtle =inactive]:text-fg-neutral-minimal",
                )}
              >
                {i18n.get("newInformation")}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-4 px-6 py-3 shrink-0">
            <div className="flex items-center gap-4">
              <span id="unread-only-label-panel" className="label-s-medium text-fg-neutral-strong">
                {i18n.get("unreadOnly")}
              </span>
              <Switch isOn={unreadOnly} onChange={setUnreadOnly} aria-labelledby="unread-only-label-panel" />
            </div>
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              disabled={isMarkingAllAsRead}
              className={cn(
                "flex-1 text-right label-s-medium text-fg-brand-subtle underline hover:text-fg-brand-strong transition-colors focus-blue",
                isMarkingAllAsRead && "opacity-50 cursor-not-allowed",
              )}
            >
              {isMarkingAllAsRead ? "Loading..." : i18n.get("markAllAsRead")}
            </button>
          </div>

          {/* Notification List - Scrollable */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 px-6">
                <p className="label-m-medium text-fg-neutral-subtle text-center">
                  Loading...
                </p>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12 px-6">
                <p className="label-m-medium text-red-600 text-center">
                  {i18n.get("errorLoadingNotifications")}
                </p>
              </div>
            ) : (
              <>
                <TabsContent value="all" className="mt-0">
                  <NotificationList
                    notifications={transformedNotifications}
                    onItemClick={handleNotificationClick}
                    onMarkAsRead={handleMarkAsReadFromMenu}
                  />
                </TabsContent>
                <TabsContent value="live_alerts" className="mt-0">
                  <NotificationList
                    notifications={transformedNotifications}
                    onItemClick={handleNotificationClick}
                    onMarkAsRead={handleMarkAsReadFromMenu}
                  />
                </TabsContent>
                <TabsContent value="new_information" className="mt-0">
                  <NotificationList
                    notifications={transformedNotifications}
                    onItemClick={handleNotificationClick}
                    onMarkAsRead={handleMarkAsReadFromMenu}
                  />
                </TabsContent>
              </>
            )}
          </div>
        </Tabs>

        {/* Footer */}
        <footer className="flex items-center gap-4 min-h-[64px] px-6 py-3 border-t border-border-token-brand-minimal shrink-0 bg-bg-brand-secondary-default">
          <button
            type="button"
            onClick={onManageClick}
            className="flex-1 label-m-semibold text-fg-neutral-strong text-left hover:text-fg-brand-strong transition-colors focus-blue"
          >
            {i18n.get("manageNotifications")}
          </button>
          <button
            type="button"
            onClick={onViewAllClick}
            className={cn(
              "h-11 px-4 rounded-lg",
              "bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover",
              "label-m-semibold text-fg-neutral-onFilled",
              "transition-colors focus-blue",
            )}
          >
            {i18n.get("viewAllNotifications")}
          </button>
        </footer>
        </div>
      </FocusTrap>
    </>,
    document.body,
  );
}

// ============================================================================
// Notification List Component
// ============================================================================

interface NotificationListProps {
  notifications: NotificationItemData[];
  onItemClick: (id: string, slug?: string) => void;
  onMarkAsRead: (id: string) => void;
}

function NotificationList({
  notifications,
  onItemClick,
  onMarkAsRead,
}: NotificationListProps) {
  const i18n = useI18N();
  
  if (notifications.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 px-6">
        <p className="label-m-medium text-fg-neutral-subtle text-center">
          {i18n.get("noNotificationsToDisplay")}
        </p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col">
      {notifications.map((notification, index) => (
        <React.Fragment key={notification.id}>
          <li>
            <NotificationItem
              data={notification}
              onClick={() => onItemClick(notification.id, notification.slug)}
              onMarkAsRead={onMarkAsRead}
            />
          </li>
          {index < notifications.length - 1 && (
            <li role="separator" aria-hidden="true">
              <div className="h-px border border-border-token-neutral-minimal" />
            </li>
          )}
        </React.Fragment>
      ))}
    </ul>
  );
}

export default NotificationPanel;
